/**
 * Tabs component using Radix UI
 * Clean, minimal design
 */

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import { forwardRef } from 'react';

const Tabs = TabsPrimitive.Root;

const TabsList = forwardRef<
    ElementRef<typeof TabsPrimitive.List>,
    ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.List
        ref={ref}
        className={cn(
            'inline-flex items-center gap-1 border-b border-[#E2E8F0] dark:border-[#334155]',
            className
        )}
        {...props}
    />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = forwardRef<
    ElementRef<typeof TabsPrimitive.Trigger>,
    ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
            'inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-11 sm:min-h-0',
            'text-sm font-medium whitespace-nowrap',
            'text-[#64748B] hover:text-[#1E293B] dark:text-[#94A3B8] dark:hover:text-[#F1F5F9]',
            'border-b-2 border-transparent -mb-px',
            'transition-colors duration-150',
            'focus-visible:outline-none',
            'data-[state=active]:font-semibold data-[state=active]:rounded-t-md',
            'data-[state=active]:bg-[#1D4ED8]/[0.06] dark:data-[state=active]:bg-[#60A5FA]/[0.16]',
            'data-[state=active]:text-[#1D4ED8] data-[state=active]:border-[#1D4ED8] dark:data-[state=active]:text-[#93C5FD] dark:data-[state=active]:border-[#60A5FA]',
            className
        )}
        {...props}
    />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef<
    ElementRef<typeof TabsPrimitive.Content>,
    ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'pt-6 focus-visible:outline-none',
            className
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
